import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, ShieldAlert, MailWarning, UserX, ChevronRight, Activity, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SignInActivityWidget = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // 'stale_invites' | 'dormant_users' | 'failed_logins_24h' | null

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/signin-activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (_e) {
      // Non-master-admins get 403; widget silently hides itself
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Card className="border-0 shadow-lg mb-8" data-testid="signin-activity-loading">
        <CardContent className="p-8 flex items-center justify-center text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading sign-in activity…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null; // 403 or fetch error — hide widget entirely

  const stale = data.stale_invites || { count: 0, items: [] };
  const dormant = data.dormant_users || { count: 0, items: [] };
  const failed = data.failed_logins_24h || { count: 0, top: [] };

  const cards = [
    {
      key: 'stale_invites',
      icon: MailWarning,
      label: 'Pending invites (3+ days)',
      count: stale.count,
      sub: stale.count === 0 ? 'Nobody to nudge' : `${stale.count} ${stale.count === 1 ? 'person' : 'people'} haven't set their password`,
      tone: stale.count > 0 ? 'amber' : 'neutral',
    },
    {
      key: 'dormant_users',
      icon: UserX,
      label: 'Dormant users (30+ days)',
      count: dormant.count,
      sub: dormant.count === 0 ? 'Everyone is active' : `${dormant.count} ${dormant.count === 1 ? 'account' : 'accounts'} not signed in recently`,
      tone: dormant.count > 0 ? 'zinc' : 'neutral',
    },
    {
      key: 'failed_logins_24h',
      icon: ShieldAlert,
      label: 'Failed logins (24h)',
      count: failed.count,
      sub: failed.count === 0
        ? 'No failed attempts'
        : (failed.count >= 10 ? 'Spike detected — review recent attempts' : `${failed.top.length} ${failed.top.length === 1 ? 'email' : 'emails'} affected`),
      tone: failed.count >= 10 ? 'red' : (failed.count > 0 ? 'zinc' : 'neutral'),
    },
  ];

  const toneClasses = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    red: 'bg-red-50 border-red-200 text-red-900',
    zinc: 'bg-zinc-50 border-zinc-200 text-zinc-900',
    neutral: 'bg-white border-zinc-100 text-zinc-700',
  };
  const iconToneClasses = {
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    zinc: 'bg-zinc-100 text-zinc-700',
    neutral: 'bg-emerald-50 text-emerald-700',
  };

  return (
    <Card className="border-0 shadow-lg mb-8" data-testid="signin-activity-widget">
      <CardHeader className="border-b border-zinc-100 pb-3">
        <CardTitle className="text-lg font-heading flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#F56F05]" />
          Sign-in activity
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {cards.map(c => {
            const Icon = c.icon;
            const isOpen = expanded === c.key;
            return (
              <button
                type="button"
                key={c.key}
                onClick={() => setExpanded(isOpen ? null : c.key)}
                className={`text-left rounded-xl border-2 p-4 transition-all hover:shadow-md ${toneClasses[c.tone]} ${isOpen ? 'ring-2 ring-[#F56F05]/40' : ''}`}
                data-testid={`signin-card-${c.key}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className={`w-9 h-9 rounded-lg ${iconToneClasses[c.tone]} flex items-center justify-center`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
                <p className="text-2xl font-bold mt-3">{c.count}</p>
                <p className="text-[11px] font-semibold uppercase tracking-wider opacity-70 mt-1">{c.label}</p>
                <p className="text-xs opacity-80 mt-1 leading-snug">{c.sub}</p>
              </button>
            );
          })}
        </div>

        {/* Expanded panels */}
        {expanded === 'stale_invites' && (
          <ExpandedPanel emptyText="No pending invites — everyone you've invited has signed in." testId="signin-panel-stale-invites">
            {stale.items.map(u => (
              <SigninUserLink
                key={u.id}
                user={u}
                hoverClass="hover:bg-amber-50"
                testIdPrefix="signin-stale"
                badgeClass="bg-amber-100 text-amber-800 border-amber-200"
                trailingText={`${u.days_since_invited}d`}
                trailingClass="text-amber-700"
              />
            ))}
          </ExpandedPanel>
        )}

        {expanded === 'dormant_users' && (
          <ExpandedPanel emptyText="All active staff have signed in within the last 30 days." testId="signin-panel-dormant">
            {dormant.items.map(u => (
              <SigninUserLink
                key={u.id}
                user={u}
                hoverClass="hover:bg-zinc-50"
                testIdPrefix="signin-dormant"
                badgeClass="bg-zinc-100 text-zinc-700 border-zinc-200"
                trailingText={u.last_login_at ? `${u.days_dormant}d ago` : 'Never'}
                trailingClass="text-zinc-600"
              />
            ))}
          </ExpandedPanel>
        )}

        {expanded === 'failed_logins_24h' && (
          <ExpandedPanel emptyText="No failed login attempts in the last 24 hours." testId="signin-panel-failed">
            {failed.top.map(row => (
              <FailedLoginRow key={row.email} row={row} />
            ))}
          </ExpandedPanel>
        )}
      </CardContent>
    </Card>
  );
};

const SigninUserLink = ({ user: u, hoverClass, testIdPrefix, badgeClass, trailingText, trailingClass }) => (
  <Link
    to={`/admin/users/${u.id}`}
    className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors ${hoverClass}`}
    data-testid={`${testIdPrefix}-${u.id}`}
  >
    <div className="min-w-0">
      <p className="text-sm font-semibold text-zinc-900 truncate">{u.full_name || u.email}</p>
      <p className="text-[11px] text-zinc-500 truncate">{u.email}</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <Badge className={`${badgeClass} capitalize text-[10px]`}>{u.role}</Badge>
      <span className={`text-xs font-medium ${trailingClass}`}>{trailingText}</span>
      <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
    </div>
  </Link>
);

const FailedLoginRow = ({ row }) => (
  <div
    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg"
    data-testid={`signin-failed-${row.email}`}
  >
    <div className="min-w-0">
      <p className="text-sm font-semibold text-zinc-900 truncate">{row.email}</p>
      <p className="text-[11px] text-zinc-500">
        Last attempt {new Date(row.last_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
      </p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className={`text-xs font-bold ${row.count >= 5 ? 'text-red-600' : 'text-zinc-700'}`}>
        {row.count} {row.count === 1 ? 'attempt' : 'attempts'}
      </span>
    </div>
  </div>
);

const ExpandedPanel = ({ children, emptyText, testId }) => {
  const hasChildren = React.Children.count(children) > 0 && React.Children.toArray(children).some(c => c);
  return (
    <div className="rounded-xl border border-zinc-100 bg-white divide-y divide-zinc-50" data-testid={testId}>
      {hasChildren ? children : (
        <p className="text-sm text-zinc-500 text-center py-6 px-4">{emptyText}</p>
      )}
    </div>
  );
};

export default SignInActivityWidget;
